import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import styled from 'styled-components';
import numeral from 'numeral';

import useScreenQuery from '../../../hooks/useScreenQuery';

dayjs.extend(localizedFormat);

const PortalLimitsHistory = ({ limits, isUS, errorMessage }: PortalLimitsHistoryProps) => {
	const { isSmall } = useScreenQuery();

	if (limits.length === 0 || errorMessage)
		return <EmptyText>{errorMessage ?? 'Your limits history is empty'}</EmptyText>;

	return (
		<Wrapper>
			{isSmall ? (
				<MobileHistory>
					{limits.map((limit) => (
						<div key={limit.id}>
							<table>
								<tbody>
									<tr>
										<th>Type:</th>
										<td>{limit?.type?.name}</td>
									</tr>
									<tr>
										<th>Period:</th>
										<td>{limit?.duration?.name}</td>
									</tr>
									<tr>
										<th>Action:</th>
										<td>{limit?.action}</td>
									</tr>
									<tr>
										<th>Time:</th>
										<td>
											{dayjs(limit.created_at)
												.locale(isUS ? 'en' : 'en-au')
												.format('L LT')}
										</td>
									</tr>
									<tr>
										<th>Old Value:</th>
										<td>{formatValue(limit.value.old, limit.type.id)}</td>
									</tr>
									<tr>
										<th>New Value:</th>
										<td>{formatValue(limit.value.new, limit.type.id)}</td>
									</tr>
								</tbody>
							</table>
						</div>
					))}
				</MobileHistory>
			) : (
				<HistoryTable>
					<thead>
						<tr>
							<th>Time</th>
							<th>Type</th>
							<th>Period</th>
							<th>Action</th>
							<th>Old</th>
							<th>New</th>
						</tr>
					</thead>
					<tbody>
						{limits.map((limit) => (
							<tr key={limit.id}>
								<td>
									{dayjs(limit.created_at)
										.locale(isUS ? 'en' : 'en-au')
										.format('L LT')}
								</td>
								<td>{limit?.type?.name}</td>
								<td>{limit?.duration?.name}</td>
								<td>{limit?.action}</td>
								<td>{formatValue(limit.value.old, limit.type.id)}</td>
								<td>{formatValue(limit.value.new, limit.type.id)}</td>
							</tr>
						))}
					</tbody>
				</HistoryTable>
			)}
		</Wrapper>
	);
};

export default PortalLimitsHistory;

export interface PortalLimitsHistoryProps {
	limits: Array<LimitsHistory>;
	isUS: boolean;
	errorMessage?: string | null;
}

interface LimitsHistory {
	id: number;
	type: {
		id: number;
		name: string;
	};
	duration: {
		id: number;
		name: string;
	};
	action: string;
	value: {
		old: null | string;
		new: null | string;
	};
	actor: {
		name: string;
		id: null | number;
		type: string;
	};
	created_at: string;
}

const EmptyText = styled.p`
	color: #fff;
	text-align: center;
`;

const Wrapper = styled.div`
	max-width: 100%;
	overflow-x: auto;
	background-color: #fff;
`;

const HistoryTable = styled.table`
	width: 100%;

	thead {
		background-color: #000;
		color: #fff;

		th {
			text-transform: uppercase;
			text-align: left;
			font-weight: 500;
			padding: 0.5rem 1rem;
		}
	}

	tbody {
		tr:nth-child(even) {
			background-color: #f2f2f2;
		}

		td {
			padding: 0.5rem 1rem;
			color: #000;
		}
	}
`;

const MobileHistory = styled.div`
	padding: 0 0.25rem;

	div {
		padding: 1rem 0;
		&:not(:first-child) {
			border-top: 1px solid #c7c7c7;
		}

		table {
			width: 100%;

			tbody {
				th {
					font-size: 1.1rem;
					padding: 0.5rem 0;
					font-weight: 500;
					color: #626262;
					text-align: left;
				}

				td {
					font-size: 1.1rem;
					padding: 0.5rem 0;
					color: #000;
					text-align: right;
				}
			}
		}
	}
`;

const formatValue = (value: string | null, typeId: number) => {
	if (typeId === 1 || typeId === 2) {
		// 1 = Deposit, 2 = Spending (other limits might not be about currency)

		// note: we can't just use formatCurrency here because the limit is not in cents
		return value ? numeral(value).format('$0,000.00') : '(No limit)';
	}

	return value ?? '(No limit)';
};
